import moment from "moment";
import type { Moment } from "moment";
import type { EmptyStateVariants } from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { AuthStore } from "stores/AuthStore";

export const DRIVERS_CUTOVER_DATE = "2026-07-01";

export type AvailabilityDateRange = "pre" | "spanning" | "post" | "unknown";

export type AvailabilityInfoKind =
    | "driversSpanning"
    | "topicsSpanning"
    | "driversLateArrival";

type AvailabilityDate = string | Date | Moment | undefined;

export type DataAvailabilityPolicyContext = {
    startDate: AvailabilityDate;
    endDate: AvailabilityDate;
    hasRenderableData: boolean;
    defaultEmptyStateVariant: EmptyStateVariants;
};

type PermissionAwarePolicy = {
    requiredPermissions?: readonly string[];
};

export type DataAvailabilityPolicy = PermissionAwarePolicy & {
    resolve: (context: DataAvailabilityPolicyContext) => DataAvailability;
};

type ResolveDataAvailabilityInput = DataAvailabilityPolicyContext & {
    authStore: AuthStore;
    policy?: DataAvailabilityPolicy;
};

export type DataAvailability = {
    emptyStateVariant: EmptyStateVariants;
    infoKind?: AvailabilityInfoKind;
};

export type AvailabilityFilterBannerKind =
    | "drivers"
    | "topics"
    | "driversSpanning"
    | "topicsSpanning"
    | "mixedSpanning";

type AvailabilityFilterSnapshot = Pick<
    ApplicationFilters,
    | "startDate"
    | "endDate"
    | "drivers"
    | "themes"
    | "primaryTopics"
    | "topics"
>;

type AvailabilityFilterBannerPolicy = PermissionAwarePolicy & {
    resolve: (
        filters: AvailabilityFilterSnapshot,
    ) => AvailabilityFilterBannerKind | undefined;
};

const hasRequiredPermissions = (
    authStore: AuthStore,
    policy: PermissionAwarePolicy,
): boolean =>
    policy.requiredPermissions?.every((permission) =>
        authStore.canUserView(permission),
    ) ?? true;

const toLocalCalendarDayKey = (value: AvailabilityDate): number | undefined => {
    if (!value) return undefined;

    if (typeof value === "string") {
        // Request dates are normally ISO strings. Reading the date prefix preserves the
        // calendar day represented by the request instead of shifting it through UTC.
        const isoDate = /^(\d{4})-(\d{2})-(\d{2})(?:T|\s|$)/.exec(value);
        if (isoDate) {
            const parsed = moment(
                `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`,
                "YYYY-MM-DD",
                true,
            );
            if (!parsed.isValid()) return undefined;
            return (
                Number(isoDate[1]) * 10000 +
                Number(isoDate[2]) * 100 +
                Number(isoDate[3])
            );
        }
    }

    const parsed = moment.isMoment(value) ? value.clone() : moment(value);
    if (!parsed.isValid()) return undefined;
    return parsed.year() * 10000 + (parsed.month() + 1) * 100 + parsed.date();
};

export const classifyAvailabilityDateRange = (
    startDate: AvailabilityDate,
    endDate: AvailabilityDate,
    cutoverDayString: string
): AvailabilityDateRange => {
    const cutoverDayNumber = Number(cutoverDayString.replace(/-/g, ""));

    const startDay = toLocalCalendarDayKey(startDate);
    const endDay = toLocalCalendarDayKey(endDate);

    if (startDay === undefined || endDay === undefined || startDay > endDay) {
        return "unknown";
    }
    if (endDay < cutoverDayNumber) return "pre";
    if (startDay >= cutoverDayNumber) return "post";
    return "spanning";
};

export const driversDataAvailabilityPolicy: DataAvailabilityPolicy = {
    requiredPermissions: ["Drivers"],
    resolve: ({
        startDate,
        endDate,
        hasRenderableData,
        defaultEmptyStateVariant,
    }) => {
        const dateRange = classifyAvailabilityDateRange(
            startDate,
            endDate,
            DRIVERS_CUTOVER_DATE,
        );

        if (dateRange === "pre") {
            return hasRenderableData
                ? {
                      emptyStateVariant: defaultEmptyStateVariant,
                      infoKind: "driversLateArrival",
                  }
                : { emptyStateVariant: "driversCutover" };
        }
        if (dateRange === "spanning" && hasRenderableData) {
            return {
                emptyStateVariant: defaultEmptyStateVariant,
                infoKind: "driversSpanning",
            };
        }
        return { emptyStateVariant: defaultEmptyStateVariant };
    },
};

export const topicsDataAvailabilityPolicy: DataAvailabilityPolicy = {
    requiredPermissions: ["Drivers"],
    resolve: ({
        startDate,
        endDate,
        hasRenderableData,
        defaultEmptyStateVariant,
    }) => {
        const dateRange = classifyAvailabilityDateRange(
            startDate,
            endDate,
            DRIVERS_CUTOVER_DATE,
        );

        if (dateRange === "spanning" && hasRenderableData) {
            return {
                emptyStateVariant: defaultEmptyStateVariant,
                infoKind: "topicsSpanning",
            };
        }
        if (dateRange === "post" && !hasRenderableData) {
            return { emptyStateVariant: "topicsCutover" };
        }
        return { emptyStateVariant: defaultEmptyStateVariant };
    },
};

export const resolveDataAvailability = ({
    authStore,
    policy,
    ...context
}: ResolveDataAvailabilityInput): DataAvailability => {
    if (!policy || !hasRequiredPermissions(authStore, policy)) {
        return { emptyStateVariant: context.defaultEmptyStateVariant };
    }

    return policy.resolve(context);
};

const availabilityFilterBannerPolicy: AvailabilityFilterBannerPolicy = {
    requiredPermissions: ["Drivers"],
    resolve: (filters) => {
        const dateRange = classifyAvailabilityDateRange(
            filters.startDate,
            filters.endDate,
            DRIVERS_CUTOVER_DATE,
        );
        const hasDriversFilter =
            (filters.drivers?.length ?? 0) > 0 ||
            (filters.themes?.length ?? 0) > 0;
        const hasTopicsFilter =
            (filters.primaryTopics?.length ?? 0) > 0 ||
            (filters.topics?.length ?? 0) > 0;

        if (dateRange === "pre" && hasDriversFilter) return "drivers";
        if (dateRange === "post" && hasTopicsFilter) return "topics";
        if (dateRange === "spanning") {
            if (hasDriversFilter && hasTopicsFilter) return "mixedSpanning";
            if (hasDriversFilter) return "driversSpanning";
            if (hasTopicsFilter) return "topicsSpanning";
        }
        return undefined;
    },
};

export const resolveAvailabilityFilterBanner = (
    filters: AvailabilityFilterSnapshot | null | undefined,
    authStore: AuthStore,
): AvailabilityFilterBannerKind | undefined => {
    if (
        !filters ||
        !hasRequiredPermissions(authStore, availabilityFilterBannerPolicy)
    ) {
        return undefined;
    }

    return availabilityFilterBannerPolicy.resolve(filters);
};
